import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subject } from 'rxjs';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface VersionInfo {
  version: string;
  buildDate: string;
  gitCommit: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private currentVersion = environment.version;
  private checkInterval = 5 * 60 * 1000; // 5 minutes
  private isChecking = false;

  constructor(private http: HttpClient) {}

  /**
   * Start periodic version checking
   * @param showNotification - Show notification before reload
   * @param autoReload - Automatically reload when new version is detected
   */
  startVersionCheck(showNotification = true, autoReload = true): void {
    if (this.isChecking) {
      return;
    }

    this.isChecking = true;
    console.log(`🔍 Version check started. Current version: ${this.currentVersion}`);

    // Check immediately
    this.checkVersion(showNotification, autoReload);

    // Then check every 5 minutes
    interval(this.checkInterval)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.getRemoteVersion())
      )
      .subscribe({
        next: (remoteVersion) => {
          this.handleVersionCheck(remoteVersion, showNotification, autoReload);
        },
        error: (err) => {
          console.error('❌ Version check failed:', err);
        }
      });
  }

  /**
   * Check version once
   */
  private checkVersion(showNotification: boolean, autoReload: boolean): void {
    this.getRemoteVersion().subscribe({
      next: (remoteVersion) => {
        this.handleVersionCheck(remoteVersion, showNotification, autoReload);
      },
      error: (err) => {
        console.error('❌ Version check failed:', err);
      }
    });
  }

  /**
   * Get remote version from server
   */
  private getRemoteVersion() {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    return this.http.get<VersionInfo>(`/version.json?t=${timestamp}`).pipe(
      catchError((error) => {
        console.error('Failed to fetch version.json:', error);
        throw error;
      })
    );
  }

  /**
   * Handle version check result
   */
  private handleVersionCheck(
    remoteVersion: VersionInfo,
    showNotification: boolean,
    autoReload: boolean
  ): void {
    if (remoteVersion.version !== this.currentVersion) {
      console.log(`🆕 New version detected: ${remoteVersion.version} (current: ${this.currentVersion})`);

      if (showNotification) {
        this.showUpdateNotification(remoteVersion, autoReload);
      } else if (autoReload) {
        this.reloadApplication();
      }
    } else {
      console.log(`✅ Version up to date: ${this.currentVersion}`);
    }
  }

  /**
   * Show update notification
   */
  private showUpdateNotification(remoteVersion: VersionInfo, autoReload: boolean): void {
    const message = `มีเวอร์ชันใหม่ v${remoteVersion.version} ${autoReload ? 'ระบบจะรีโหลดอัตโนมัติใน 5 วินาที' : 'กรุณารีโหลดหน้าเว็บ'}`;

    // Show browser notification if supported
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('อัปเดตเวอร์ชันใหม่', {
        body: message,
        icon: '/im_logo.png'
      });
    }

    // Show console message
    console.log(`📢 ${message}`);

    // Show alert
    if (confirm(message)) {
      this.reloadApplication();
    } else if (autoReload) {
      // Auto reload after 5 seconds if user doesn't cancel
      setTimeout(() => {
        this.reloadApplication();
      }, 5000);
    }
  }

  /**
   * Reload the application
   */
  private reloadApplication(): void {
    console.log('🔄 Reloading application...');

    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Hard reload
    window.location.reload();
  }

  /**
   * Stop version checking
   */
  stopVersionCheck(): void {
    this.isChecking = false;
    this.destroy$.next();
    console.log('⏹️ Version check stopped');
  }

  ngOnDestroy(): void {
    this.stopVersionCheck();
    this.destroy$.complete();
  }
}
